import { type FormEvent, useState } from 'react';
import type { NotificationTopic } from '@gobingebox/shared';
import { Button } from '@/components/ui/Button';
import { Bell } from '@/components/icons';
import { notifyApi } from '@/features/notifications/api';

/** "Notify me" opt-in form for empty Store / Games pages (spec §12). */
export function NotifyForm({ topic }: { topic: NotificationTopic }) {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.includes('@')) return;
    setBusy(true);
    try {
      await notifyApi.optIn(topic, email);
      setSubmitted(true);
    } catch {
      setSubmitted(true); // dedupe/errors shouldn't block the friendly confirmation
    } finally {
      setBusy(false);
    }
  }

  if (submitted) {
    return (
      <p className="flex items-center gap-2 text-sm text-success">
        <Bell size={16} />
        You're on the list — we'll email you the moment it's live.
      </p>
    );
  }

  return (
    <form className="flex w-full max-w-md flex-col gap-2 sm:flex-row" onSubmit={onSubmit}>
      <label htmlFor={`notify-${topic}`} className="sr-only">
        Email address
      </label>
      <input
        id={`notify-${topic}`}
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        className="h-11 flex-1 rounded-full border border-border bg-surface px-4 text-sm text-ink placeholder:text-ink-faint focus:border-brand"
      />
      <Button type="submit" size="md" className="h-11" disabled={busy}>
        <Bell size={16} />
        {busy ? 'Saving…' : 'Notify me'}
      </Button>
    </form>
  );
}
